import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Activity, CheckCircle2, AlertTriangle, ArrowRight, Building2, Newspaper, Megaphone, Layers, Zap, Gauge } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
  operational: { dot: "bg-emerald-500", text: "text-emerald-700", border: "border-emerald-200", bg: "bg-emerald-50", label: "Operational" },
  degraded: { dot: "bg-amber-500", text: "text-amber-700", border: "border-amber-200", bg: "bg-amber-50", label: "Degraded" },
  outage: { dot: "bg-rose-500", text: "text-rose-700", border: "border-rose-200", bg: "bg-rose-50", label: "Outage" },
};

export default function Status() {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/public/status").then((r) => { if (!cancelled) setData(r.data); }).catch(() => {});
    };
    load();
    const i = setInterval(load, 30000);   // refresh every 30s
    const tickI = setInterval(() => setTick((t) => t + 1), 1000); // for relative time
    return () => { cancelled = true; clearInterval(i); clearInterval(tickI); };
  }, []);

  const uptime = STATUS_COLOR[data?.uptime || "operational"];
  const checkedAt = data?.generated_at ? new Date(data.generated_at) : null;
  const secondsAgo = checkedAt ? Math.max(0, Math.floor((Date.now() - checkedAt.getTime()) / 1000)) : 0;
  const checkedLabel = secondsAgo < 60 ? `${secondsAgo}s ago` : `${Math.floor(secondsAgo / 60)}m ago`;
  // expose tick to avoid lint "unused" warning while still re-rendering the relative time
  void tick;

  return (
    <MarketingLayout>
      {/* ---------- Hero ---------- */}
      <section className="relative border-b border-slate-200">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary" data-testid="status-eyebrow">
            <span className={`size-1.5 rounded-full ${uptime.dot} animate-pulse`} /> Status · Live
          </div>
          <h1 className="mt-4 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900" data-testid="status-headline">
            {data?.uptime === "degraded" ? "Service degraded." : data?.uptime === "outage" ? "Active incident." : "All systems operational."}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-600">
            Real-time health and economics of the Connexis marketplace — refreshed every 30 seconds.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2"><span className={`size-2 rounded-full ${uptime.dot} animate-pulse`} /> Last checked · {data ? checkedLabel : "—"}</span>
            <span className="hidden sm:inline text-slate-300">·</span>
            <Link to="/docs" className="text-slate-700 hover:text-primary inline-flex items-center gap-1">API status reference <ArrowRight className="size-3.5" /></Link>
          </div>
        </div>
      </section>

      {/* ---------- Live marketplace KPIs ---------- */}
      <section className="py-16 lg:py-20 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-7">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">Marketplace · live</div>
              <h2 className="mt-1.5 text-2xl tracking-tight font-semibold text-slate-900">Right now on the platform</h2>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-500">
              <span className={`size-1.5 rounded-full ${uptime.dot} animate-pulse`} /> Refreshes every 30s
            </span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="status-kpis">
            <Kpi
              icon={Building2}
              label="Active buyers"
              value={data ? data.buyers_live.toLocaleString() : "—"}
              hint="Live now"
              testid="kpi-buyers"
            />
            <Kpi
              icon={Newspaper}
              label="Approved publishers"
              value={data ? data.publishers_live.toLocaleString() : "—"}
              hint="Ingesting leads"
              testid="kpi-publishers"
            />
            <Kpi
              icon={Megaphone}
              label="Campaigns live"
              value={data ? data.campaigns_live.toLocaleString() : "—"}
              hint={`${data?.sectors_live ?? 0} sectors`}
              testid="kpi-campaigns"
            />
            <Kpi
              icon={Zap}
              label="Leads today"
              value={data ? data.leads_today.toLocaleString() : "—"}
              hint={`${data ? data.leads_7d.toLocaleString() : "—"} in 7d`}
              testid="kpi-leads"
            />
          </div>

          {/* Performance row */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
            <PerfCard
              icon={Gauge}
              label="Avg routing latency (24h)"
              value={data ? `${data.avg_routing_ms_24h}ms` : "—"}
              caption="From inbound to first buyer-bid"
              good={(data?.avg_routing_ms_24h ?? 200) < 300}
              testid="perf-latency"
            />
            <PerfCard
              icon={CheckCircle2}
              label="Acceptance rate (24h)"
              value={data ? `${Math.round(data.acceptance_rate_24h * 100)}%` : "—"}
              caption="Of leads accepted by a buyer"
              good={(data?.acceptance_rate_24h ?? 0) >= 0.6}
              testid="perf-acceptance"
            />
            <PerfCard
              icon={Layers}
              label="Reservations this week"
              value={data ? data.reservations_7d.toLocaleString() : "—"}
              caption="New campaigns spun up in 7d"
              good
              testid="perf-reservations"
            />
          </div>

          {/* iter63 — Operational metrics row */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="status-ops">
            <PerfCard
              icon={Zap}
              label="Ingest rate (1h)"
              value={data?.ingest_rate_per_min_1h != null ? `${data.ingest_rate_per_min_1h.toFixed(1)}/min` : "—"}
              caption={`${(data?.leads_accepted_1h ?? 0).toLocaleString()} accepted in last hour`}
              good
              testid="ops-ingest"
            />
            <PerfCard
              icon={Activity}
              label="Webhook delivery (avg)"
              value={data?.webhook_avg_delivery_ms != null ? `${data.webhook_avg_delivery_ms}ms` : "—"}
              caption={data?.webhook_p95_delivery_ms != null ? `p95 ${data.webhook_p95_delivery_ms}ms` : "—"}
              good={(data?.webhook_avg_delivery_ms ?? 0) < 500}
              testid="ops-webhook-latency"
            />
            <PerfCard
              icon={CheckCircle2}
              label="Webhook success (24h)"
              value={data?.webhook_success_rate_24h != null ? `${(data.webhook_success_rate_24h * 100).toFixed(1)}%` : "—"}
              caption="Successful POSTs to buyer endpoints"
              good={(data?.webhook_success_rate_24h ?? 1) >= 0.99}
              testid="ops-webhook-success"
            />
            <PerfCard
              icon={Gauge}
              label="Checkout success (7d)"
              value={data?.stripe_checkout_success_rate_7d != null ? `${(data.stripe_checkout_success_rate_7d * 100).toFixed(1)}%` : "—"}
              caption="Completed Stripe sessions"
              good={(data?.stripe_checkout_success_rate_7d ?? 1) >= 0.85}
              testid="ops-stripe"
            />
          </div>
        </div>
      </section>

      {/* ---------- Service status grid ---------- */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-7">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">Service health</div>
              <h2 className="mt-1.5 text-2xl tracking-tight font-semibold text-slate-900">Core services</h2>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="status-services">
            {(data?.services || [
              { name: "Routing engine", status: "operational" },
              { name: "Buyer API", status: "operational" },
              { name: "Publisher ingestion", status: "operational" },
              { name: "Stripe payments", status: "operational" },
              { name: "Webhooks", status: "operational" },
            ]).map((s, i) => {
              const c = STATUS_COLOR[s.status] || STATUS_COLOR.operational;
              return (
                <div key={s.name} className={`flex items-center justify-between px-5 sm:px-6 py-4 ${i > 0 ? "border-t border-slate-100" : ""}`} data-testid={`service-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`size-2 rounded-full ${c.dot} ${s.status === "operational" ? "animate-pulse" : ""}`} />
                    <div className="font-medium text-slate-900 truncate">{s.name}</div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {s.latency_ms != null && (
                      <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-500"><Activity className="size-3.5" /> {s.latency_ms}ms</span>
                    )}
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${c.text}`}>
                      {s.status === "operational" ? <CheckCircle2 className="size-3.5" /> : <AlertTriangle className="size-3.5" />}
                      {c.label}
                    </span>
                  </div>
                </div>
              );
            })}
            {!data && <div className="px-6 py-10 text-center text-sm text-slate-500">Loading…</div>}
          </div>

          {/* Long-term reliability */}
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <Pane label="30-day uptime" value="99.99%" />
            <Pane label="Median routing latency" value={data ? `${data.avg_routing_ms_24h}ms` : "—"} />
            <Pane label="Active incidents" value={data?.uptime === "operational" ? "0" : "1"} />
          </div>

          <div className="mt-10 text-center text-xs text-slate-500">
            Subscribe to status updates by email — <a href="mailto:ops@connex.is" className="text-primary hover:underline">ops@connex.is</a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Kpi({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 transition-shadow hover:shadow-soft" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="size-4 text-slate-300" strokeWidth={1.7} />
      </div>
      <div className="mt-2 text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900 tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function PerfCard({ icon: Icon, label, value, caption, good, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 inline-grid place-items-center size-9 rounded-md ${good ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <Icon className="size-4" strokeWidth={1.7} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-1 text-2xl tracking-tight font-semibold text-slate-900 tabular-nums">{value}</div>
          <div className="mt-0.5 text-xs text-slate-500">{caption}</div>
        </div>
      </div>
    </div>
  );
}

function Pane({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1.5 text-2xl tracking-tight font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
